import React from "react";
import { useTranslation } from "react-i18next";
import { IconX, IconSparkles, IconEdit } from "@tabler/icons-react";
import MDialog from "@/components/ui/atoms/MDialog";
import { MButton } from "@/components/ui/atoms/MButtons";
import actionableInsightsService, {
  ActionableInsight,
} from "@/services/actionable-insights.service";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { formatPrettyDateNoOrdinal } from "@/utils/date-utils";
import { setNativeKeyboardAvoid } from "@/utils/nativeBridge";
import styles from "./MAIModal.module.css";

export type AIModalVariant = "prescription" | "labReport";

interface MAIModalProps {
  open: boolean;
  onClose: () => void;
  content?: string;
  whatsNextTitle?: string;
  whatsNextContent?: string | string[];
  variant?: AIModalVariant;
  insightsData?: ActionableInsight[];
  onInsightUpdated?: (updated: ActionableInsight) => void;
}

const MAX_SECTION_LENGTH = 250;

const EDITABLE_TYPES = new Set([
  "PRESCRIPTION_SUMMARY",
  "DISCHARGE_SUMMARY_INSIGHT",
  "LAB_SUMMARY_PROCEDURE",
  "LAB_SUMMARY_IMAGING",
  "LAB_SUMMARY_ALLERGY",
]);

const PRESCRIPTION_SECTION_KEYS = [
  "reason_for_visit",
  "diagnosis",
  "medicines",
  "whats_next",
] as const;

const PRESCRIPTION_SECTION_TITLES: Record<
  (typeof PRESCRIPTION_SECTION_KEYS)[number],
  string
> = {
  reason_for_visit: "Reason for visit",
  diagnosis: "Diagnosis",
  medicines: "Medicines",
  whats_next: "What's next",
};

const DISCHARGE_FIELDS: Array<{ key: string; title: string }> = [
  { key: "reason_for_admission", title: "Reason for Admission" },
  { key: "key_diagnosis", title: "Key Diagnosis" },
  { key: "treatment_procedure_done", title: "Treatment/Procedure Done" },
  { key: "discharge_advice_followup", title: "What's Next" },
];

const PROCEDURE_FIELDS: Array<{ key: string; title: string }> = [
  { key: "indication", title: "Indication" },
  { key: "procedure_performed", title: "Procedure Performed" },
  { key: "key_findings", title: "Key Findings" },
  { key: "next_steps", title: "What's Next" },
];

const IMAGING_FIELDS: Array<{ key: string; title: string }> = [
  { key: "body_part_imaged", title: "What was tested" },
  { key: "test_method", title: "Test Method" },
  { key: "key_findings", title: "Key Findings" },
  { key: "clinical_impression", title: "Clinical Impression" },
];

const ALLERGY_FIELDS: Array<{ key: string; title: string }> = [
  { key: "test_method", title: "Test Method" },
  { key: "allergen_panel", title: "Allergen Panel" },
  { key: "positive_allergens", title: "Positive Allergens" },
  { key: "recommendations", title: "Recommendations" },
];

const shouldHideContent = (text?: string): boolean => {
  if (!text || text.trim() === "") return true;

  const normalizedText = text
    .toLowerCase()
    .trim()
    .replace(/[.,;:!?]+$/, "");

  const exactMatchPatterns = [
    "not available",
    "not clearly mentioned",
    "not mentioned",
    "not specified",
    "information not available",
    "data not available",
    "not provided",
    "n/a",
    "na",
    "none",
    "nil",
    "not applicable",
    "not found",
  ];

  return exactMatchPatterns.includes(normalizedText);
};

const findEditableInsight = (
  insightsData?: ActionableInsight[]
): ActionableInsight | undefined => {
  if (!insightsData) return undefined;
  return insightsData.find((item) => EDITABLE_TYPES.has(item.type));
};

interface ParsedPrescription {
  reason_for_visit: string;
  diagnosis: string;
  medicines: string;
  whats_next: string;
}

/**
 * Parse the legacy text-blob prescription summary into four editable
 * fields. Recognises three heading variants for the diagnosis/medicines
 * split so older insights still load cleanly:
 *
 *   1. NEW (post structured-data rollout):
 *      `Diagnosis:` + `Medicines:` as separate headings → routed to the
 *      dedicated `diagnosis` / `medicines` buckets respectively.
 *
 *   2. LEGACY (pre-split): a single `Diagnosis & Medicines:` heading
 *      bundles both → routed entirely into the `diagnosis` bucket so the
 *      user sees their text and can manually split it across the two new
 *      textareas before saving.
 *
 *   3. RAW MEDICINE LINE (no heading): a heading containing "medicine"
 *      that isn't the combined "Diagnosis & Medicines" → medicines bucket.
 *
 * Order-independent — the parser walks line by line and routes by the
 * most recently seen heading.
 */
const parsePrescriptionSummary = (
  summary?: string
): ParsedPrescription => {
  const result: ParsedPrescription = {
    reason_for_visit: "",
    diagnosis: "",
    medicines: "",
    whats_next: "",
  };
  if (!summary) return result;

  // Split on "Next Steps Advised:" to isolate the what's next portion.
  const [beforeWhatsNext, afterWhatsNext] = summary.split(/Next Steps Advised:/i);
  result.whats_next = (afterWhatsNext || "").trim();

  const lines = (beforeWhatsNext || "")
    .split(/\\n|\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  let currentKey: keyof ParsedPrescription | null = null;
  const buffers: Record<keyof ParsedPrescription, string[]> = {
    reason_for_visit: [],
    diagnosis: [],
    medicines: [],
    whats_next: [],
  };

  for (const line of lines) {
    const headingMatch = line.match(/^(.+?):\s*$/);
    if (headingMatch) {
      const heading = headingMatch[1].toLowerCase();
      if (
        heading.includes("reason for visit") ||
        heading.includes("symptoms")
      ) {
        currentKey = "reason_for_visit";
      } else if (
        // "Diagnosis & Medicines:" (legacy combined heading) — route to
        // the diagnosis bucket. Checked BEFORE the bare-"medicine" branch
        // so the combined heading wins. The user can manually move the
        // medicine portion into the Medicines textarea after edit.
        heading.includes("diagnosis") &&
        heading.includes("medicine")
      ) {
        currentKey = "diagnosis";
      } else if (heading.includes("diagnosis")) {
        currentKey = "diagnosis";
      } else if (heading.includes("medicine")) {
        currentKey = "medicines";
      } else if (heading.includes("next")) {
        currentKey = "whats_next";
      } else {
        currentKey = null;
      }
      continue;
    }
    if (currentKey) {
      // Medicines render one per line in the textarea so the user can
      // edit them as a list; everything else collapses to a single
      // space-joined paragraph (matches the textarea autowrap UX).
      if (currentKey === "medicines") {
        buffers.medicines.push(line);
      } else {
        buffers[currentKey].push(line);
      }
    }
  }

  result.reason_for_visit = buffers.reason_for_visit.join(" ").trim();
  result.diagnosis = buffers.diagnosis.join(" ").trim();
  result.medicines = buffers.medicines.join("\n").trim();
  if (!result.whats_next) {
    result.whats_next = buffers.whats_next.join(" ").trim();
  }

  return result;
};

const buildPrescriptionSummary = (parsed: ParsedPrescription): string => {
  const segments: string[] = [];
  if (parsed.reason_for_visit) {
    segments.push(`Reason for visit:\n${parsed.reason_for_visit}`);
  }
  if (parsed.diagnosis) {
    segments.push(`Diagnosis:\n${parsed.diagnosis}`);
  }
  if (parsed.medicines) {
    segments.push(`Medicines:\n${parsed.medicines}`);
  }
  if (parsed.whats_next) {
    segments.push(`Next Steps Advised:\n${parsed.whats_next}`);
  }
  return segments.join("\n");
};

interface EditableField {
  key: string;
  title: string;
  value: string;
}

const buildEditableFields = (
  insight: ActionableInsight | undefined
): EditableField[] => {
  if (!insight) return [];

  if (insight.type === "PRESCRIPTION_SUMMARY") {
    // The text summary is always the safe fallback (it's what
    // user-edited insights store), but the structured value the OCR
    // pipeline writes (`value.symptoms`, `value.diagnosis[]`,
    // `value.medicines[]`, `value.nextSteps`) is cleaner: diagnoses
    // are already separated from medicines, and each medicine has a
    // name/dosage/frequency triple instead of being mashed into a
    // single text blob with the diagnoses.
    //
    // Strategy: start from the parsed summary, then OVERWRITE each
    // structured-derived field when the structured value is populated.
    // That way the user always sees their edits if they previously
    // saved as text (`userEdited === true`, no longer structured) but
    // sees the clean structured shape on first edit of an unedited
    // insight.
    const parsed = parsePrescriptionSummary(insight.summary);

    // User edits write back to `summary` text only — `value.*` is the original
    // pipeline output and is NOT updated on save. If the user has previously
    // edited this insight, trust the parsed summary and skip the structured
    // overrides entirely so their changes are preserved in the edit fields.
    if (!insight.userEdited) {
      const value = (insight.value ?? {}) as {
        symptoms?: string;
        diagnosis?: unknown;
        medicines?: unknown;
        nextSteps?: string;
      };

      if (typeof value.symptoms === "string" && value.symptoms.trim()) {
        parsed.reason_for_visit = value.symptoms.trim();
      }
      if (Array.isArray(value.diagnosis)) {
        const diagnosisLines = value.diagnosis
          .filter((d): d is string => typeof d === "string" && d.trim().length > 0)
          .map((d) => d.trim());
        if (diagnosisLines.length > 0) {
          parsed.diagnosis = diagnosisLines.join("\n");
        }
      }
      if (Array.isArray(value.medicines)) {
        const medicineLines = value.medicines
          .map((m) => {
            if (!m || typeof m !== "object") return "";
            const name = typeof (m as any).name === "string" ? (m as any).name.trim() : "";
            if (!name || name === "null") return "";
            const dosage =
              typeof (m as any).dosage === "string" ? (m as any).dosage.trim() : "";
            const frequency =
              typeof (m as any).frequency === "string"
                ? (m as any).frequency.trim()
                : "";
            const meta = [dosage, frequency].filter(Boolean).join(" · ");
            return meta ? `${name} — ${meta}` : name;
          })
          .filter((line) => line.length > 0);
        // Override even when empty — structured value is authoritative for
        // unedited insights so the raw "null" from the text summary never shows.
        parsed.medicines = medicineLines.join("\n");
      }
      if (typeof value.nextSteps === "string" && value.nextSteps.trim()) {
        parsed.whats_next = value.nextSteps.trim();
      }
    }

    return PRESCRIPTION_SECTION_KEYS.map((key) => ({
      key,
      title: PRESCRIPTION_SECTION_TITLES[key],
      value: parsed[key] || "",
    }));
  }

  let fields: Array<{ key: string; title: string }> = [];
  if (insight.type === "DISCHARGE_SUMMARY_INSIGHT") fields = DISCHARGE_FIELDS;
  else if (insight.type === "LAB_SUMMARY_PROCEDURE") fields = PROCEDURE_FIELDS;
  else if (insight.type === "LAB_SUMMARY_IMAGING") fields = IMAGING_FIELDS;
  else if (insight.type === "LAB_SUMMARY_ALLERGY") fields = ALLERGY_FIELDS;

  const value = insight.value || {};
  return fields.map((f) => ({
    key: f.key,
    title: f.title,
    value: typeof value[f.key] === "string" ? value[f.key] : "",
  }));
};

const MAIModal: React.FC<MAIModalProps> = ({
  open,
  onClose,
  content,
  whatsNextTitle,
  whatsNextContent,
  variant = "prescription",
  insightsData,
  onInsightUpdated,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const editableInsight = findEditableInsight(insightsData);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [fields, setFields] = React.useState<EditableField[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Reset edit mode when modal opens/closes or when insight changes.
  React.useEffect(() => {
    if (!open) {
      setEditMode(false);
      setSaving(false);
    }
  }, [open]);

  React.useEffect(() => {
    // Never rebuild fields while the user is actively editing — a parent
    // re-render can produce a new editableInsight reference (same data, new
    // object) which would reset whatever the user just typed. Fields are
    // initialised in handleEditClick when entering edit mode; this effect
    // only handles the non-editing cases (initial load and post-save reset).
    if (!editMode) {
      setFields(buildEditableFields(editableInsight));
    }
  }, [editableInsight, editMode]);

  // Allow keyboard interaction while editing — disable the native
  // KeyboardAvoidingView so the modal doesn't shake when textareas focus,
  // and re-anchor the dialog to the top of the visual viewport while the
  // keyboard is open so the focused textarea isn't trapped behind it.
  // (MDialog centers in the layout viewport, which doesn't shrink with the
  // keyboard — so we have to override that for the duration of edit mode.)
  React.useEffect(() => {
    if (!open || !editMode) return;

    setNativeKeyboardAvoid(false);

    const visualViewport = window.visualViewport;

    const overlay =
      containerRef.current?.parentElement instanceof HTMLElement
        ? containerRef.current.parentElement
        : null;

    const applyViewport = () => {
      if (!visualViewport || !containerRef.current) return;
      const margin = 12;
      containerRef.current.style.maxHeight = `${
        visualViewport.height - margin * 2
      }px`;
      if (overlay) {
        overlay.style.alignItems = "flex-start";
        overlay.style.paddingTop = `${visualViewport.offsetTop + margin}px`;
      }
    };

    const clearViewport = () => {
      if (containerRef.current) {
        containerRef.current.style.maxHeight = "";
      }
      if (overlay) {
        overlay.style.alignItems = "";
        overlay.style.paddingTop = "";
      }
    };

    applyViewport();
    visualViewport?.addEventListener("resize", applyViewport);
    visualViewport?.addEventListener("scroll", applyViewport);

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLTextAreaElement)) return;
      setTimeout(() => {
        applyViewport();
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 300);
    };
    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      visualViewport?.removeEventListener("resize", applyViewport);
      visualViewport?.removeEventListener("scroll", applyViewport);
      clearViewport();
      setNativeKeyboardAvoid(true);
    };
  }, [open, editMode]);

  const handleEditClick = () => {
    setFields(buildEditableFields(editableInsight));
    setEditMode(true);
  };

  const handleFieldChange = (key: string, value: string) => {
    if (value.length > MAX_SECTION_LENGTH) return;
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, value } : f))
    );
  };

  const handleSave = async () => {
    if (!editableInsight) return;
    setSaving(true);
    try {
      let payload: { value?: Record<string, any>; summary?: string } = {};

      if (editableInsight.type === "PRESCRIPTION_SUMMARY") {
        const parsed = fields.reduce<ParsedPrescription>(
          (acc, f) => {
            (acc as any)[f.key] = f.value.trim();
            return acc;
          },
          {
            reason_for_visit: "",
            diagnosis: "",
            medicines: "",
            whats_next: "",
          }
        );
        payload.summary = buildPrescriptionSummary(parsed);
      } else {
        const valueUpdate: Record<string, string> = {};
        fields.forEach((f) => {
          valueUpdate[f.key] = f.value.trim();
        });
        payload.value = valueUpdate;
      }

      const updated = await actionableInsightsService.updateInsight(
        editableInsight.id || editableInsight._id,
        payload
      );

      onInsightUpdated?.(updated);
      setEditMode(false);
    } catch (err) {
      console.error("Error saving AI preview:", err);
    } finally {
      setSaving(false);
    }
  };

  const isUserEdited = Boolean(editableInsight?.userEdited);
  const editedSubtitle =
    isUserEdited && editableInsight?.editedAt
      ? t("aiPreview.editedBy", {
          date: formatPrettyDateNoOrdinal(editableInsight.editedAt),
          name: editableInsight.editedByName || "",
        })
      : "";

  return (
    <MDialog
      isOpen={open}
      onClose={onClose}
      showCloseButton={false}
      title=""
      className="noPadding"
    >
      <div
        ref={containerRef}
        className={`${styles.aimodalContainer} ${
          editMode ? styles.aimodalContainerEditing : ""
        }`}
      >
        {/* Header Section */}
        <div className={styles.headerSection}>
          <div className={styles.topSection}>
            <button
              className={styles.closeButton}
              onClick={onClose}
              type="button"
              aria-label={t("common.close")}
            >
              <IconX size={20} color="#FFFFFF" />
            </button>
          </div>

          <div className={styles.iconTitleContainer}>
            <div className={styles.iconContainer}>
              <IconSparkles size={12} color="#4C189A" />
            </div>
            <h2 className={styles.aiPreviewText}>{t("aiPreview.title")}</h2>
          </div>

          {!editMode && editedSubtitle && (
            <p className={styles.editedSubtitle}>{editedSubtitle}</p>
          )}
        </div>

        {/* Body Section */}
        <div className={styles.bodySection}>
          {editMode ? (
            <div className={styles.editFieldsContainer}>
              <EditFields
                fields={fields}
                onChange={handleFieldChange}
                maxLength={MAX_SECTION_LENGTH}
                t={t}
              />
              <div className={styles.editSaveRow}>
                <MButton
                  variant="filled"
                  size="md"
                  text={saving ? t("aiPreview.saving") : t("aiPreview.save")}
                  onClick={handleSave}
                  disabled={saving}
                  loading={saving}
                />
              </div>
            </div>
          ) : (
            <>
              {editableInsight && (
                <button
                  type="button"
                  className={styles.editIconButton}
                  onClick={handleEditClick}
                  aria-label={t("aiPreview.edit")}
                >
                  <IconEdit size={16} />
                  <span>{t("aiPreview.edit")}</span>
                </button>
              )}
              {(() => {
                if (variant === "labReport") {
                  return <LabReportContent insightsData={insightsData} />;
                }
                if (
                  insightsData?.find(
                    (item) => item.type === "DISCHARGE_SUMMARY_INSIGHT"
                  )
                ) {
                  return <DischargeSummaryContent insightsData={insightsData} />;
                }
                // New structured prescription rendering: the shallow-insights
                // generator now populates `value.medicines` + `value.diagnosis`
                // alongside the legacy text `summary`. Prefer the structured
                // shape so the modal can show a numbered medicine list with
                // name/dosage/frequency instead of a single text blob. Skip
                // the structured path if the user has edited the text via
                // this same modal — those edits write back to `summary` only,
                // so we should reflect them by re-parsing the text.
                const prescriptionInsight = insightsData?.find(
                  (item) => item.type === "PRESCRIPTION_SUMMARY"
                );
                if (
                  prescriptionInsight &&
                  !prescriptionInsight.userEdited &&
                  hasStructuredPrescriptionValue(prescriptionInsight.value)
                ) {
                  return (
                    <PrescriptionStructuredContent
                      insight={prescriptionInsight}
                    />
                  );
                }
                return (
                  <AIContent
                    content={content}
                    whatsNextTitle={whatsNextTitle}
                    whatsNextContent={whatsNextContent}
                  />
                );
              })()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footerSection}>
          <p className={styles.footerText}>
            {isUserEdited && !editMode
              ? t("aiPreview.footerEdited")
              : t("aiPreview.footerDefault")}
          </p>
        </div>
      </div>
    </MDialog>
  );
};

interface EditFieldsProps {
  fields: EditableField[];
  onChange: (key: string, value: string) => void;
  maxLength: number;
  t: (key: string, options?: any) => string;
}

const EditFields: React.FC<EditFieldsProps> = ({
  fields,
  onChange,
  maxLength,
  t,
}) => {
  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      {fields.map((field) => (
        <div key={field.key} className={styles.editSection}>
          <h3 className={styles.sectionTitle}>{field.title}</h3>
          <textarea
            className={styles.editTextarea}
            value={field.value}
            maxLength={maxLength}
            onChange={(e) => onChange(field.key, e.target.value)}
            onFocus={handleFocus}
            rows={4}
          />
          <div className={styles.charCount}>
            {t("aiPreview.characterCount", {
              count: field.value.length,
              max: maxLength,
            })}
          </div>
        </div>
      ))}
    </>
  );
};

interface LabReportContentProps {
  insightsData?: ActionableInsight[];
}

const LabReportContent: React.FC<LabReportContentProps> = ({
  insightsData,
}) => {
  const labSummaryProcedure = insightsData?.find(
    (item) => item.type === "LAB_SUMMARY_PROCEDURE"
  );

  if (labSummaryProcedure) {
    const { indication, procedure_performed, key_findings, next_steps } =
      labSummaryProcedure.value;

    return (
      <>
        {indication && !shouldHideContent(indication) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Indication:</h3>
            <p className={styles.sectionContent}>{indication}</p>
          </div>
        )}
        {procedure_performed && !shouldHideContent(procedure_performed) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Procedure Performed:</h3>
            <p className={styles.sectionContent}>{procedure_performed}</p>
          </div>
        )}
        {key_findings && !shouldHideContent(key_findings) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Key Findings:</h3>
            <p className={styles.sectionContent}>{key_findings}</p>
          </div>
        )}
        {!shouldHideContent(next_steps) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>What's Next:</h3>
            {next_steps ? (
              <p className={styles.sectionContent}>{next_steps}</p>
            ) : (
              <p className={styles.sectionContent}>
                Keep reports carefully and bring them during the next hospital
                visit.
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  const labSummaryImaging = insightsData?.find(
    (item) => item.type === "LAB_SUMMARY_IMAGING"
  );

  if (labSummaryImaging) {
    const { body_part_imaged, test_method, key_findings, clinical_impression } =
      labSummaryImaging.value;

    return (
      <>
        {body_part_imaged && !shouldHideContent(body_part_imaged) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>What was tested:</h3>
            <p className={styles.sectionContent}>{body_part_imaged}</p>
          </div>
        )}
        {test_method && !shouldHideContent(test_method) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Test Method:</h3>
            <p className={styles.sectionContent}>{test_method}</p>
          </div>
        )}
        {key_findings && !shouldHideContent(key_findings) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Key Findings:</h3>
            <p className={styles.sectionContent}>{key_findings}</p>
          </div>
        )}
        {!shouldHideContent(clinical_impression) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Clinical Impression:</h3>
            {clinical_impression ? (
              <p className={styles.sectionContent}>{clinical_impression}</p>
            ) : (
              <p className={styles.sectionContent}>
                Clinical correlation advised.
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  const labSummaryAllergy = insightsData?.find(
    (item) => item.type === "LAB_SUMMARY_ALLERGY"
  );

  if (labSummaryAllergy) {
    const { test_method, allergen_panel, positive_allergens, recommendations } =
      labSummaryAllergy.value;

    return (
      <>
        {test_method && !shouldHideContent(test_method) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Test Method:</h3>
            <p className={styles.sectionContent}>{test_method}</p>
          </div>
        )}
        {allergen_panel && !shouldHideContent(allergen_panel) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Allergen Panel:</h3>
            <p className={styles.sectionContent}>{allergen_panel}</p>
          </div>
        )}
        {positive_allergens && !shouldHideContent(positive_allergens) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Positive Allergens:</h3>
            <p className={styles.sectionContent}>{positive_allergens}</p>
          </div>
        )}
        {!shouldHideContent(recommendations) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Recommendations:</h3>
            {recommendations ? (
              <p className={styles.sectionContent}>{recommendations}</p>
            ) : (
              <p className={styles.sectionContent}>
                Consult with your healthcare provider for recommendations.
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  // Blood pathology / generic LAB_SUMMARY view (no edit support).
  const formatParameterName = (name: string) =>
    name.replace(/,([^\s])/g, ", $1");

  const abnormalValues: Array<{
    parameter_name: string;
    parameter_result: string;
    parameter_unit: string;
    direction: string;
    severity: string;
  }> = [];

  if (insightsData && Array.isArray(insightsData)) {
    insightsData.forEach((item) => {
      if (item.type === "LAB_SUMMARY" && item.value?.abnormal_values) {
        item.value.abnormal_values.forEach((factor: any) => {
          const severity = String(factor.severity || "").toLowerCase();
          if (
            severity === "low" ||
            severity === "high" ||
            severity === "medium"
          ) {
            abnormalValues.push({
              parameter_name: factor.parameter_name,
              parameter_result: factor.parameter_result,
              parameter_unit: factor.parameter_unit,
              direction: factor.direction,
              severity,
            });
          }
        });
      }
    });
  }

  // Show most clinically significant first: high → medium → low severity.
  const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };
  abnormalValues.sort(
    (a, b) =>
      (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3),
  );

  return (
    <>
      <div className={styles.contentSection}>
        <p className={styles.sectionContent}>
          {abnormalValues.length > 0
            ? `This report shows ${abnormalValues.length} values outside the range`
            : "All values are within the range in this report."}
        </p>
      </div>

      <div className={styles.labReportTable}>
        <h3 className={styles.labReportTitle}>Values outside the range</h3>
        <div
          className={`${styles.labReportValues} ${
            abnormalValues.length === 0 ? styles.noValues : ""
          }`}
        >
          {abnormalValues.length > 0 ? (
            <>
              {abnormalValues.slice(0, 6).map((value, index) => (
                <div
                  key={`lab-value-${value.parameter_name
                    .slice(0, 20)
                    .replace(/\s+/g, "-")}-${index}`}
                  className={styles.labReportRow}
                >
                  <span className={styles.labReportParameter}>
                    {formatParameterName(value.parameter_name)}
                  </span>
                  <span className={styles.labReportResult}>
                    {value.parameter_result}
                    {value.parameter_unit ? ` ${value.parameter_unit}` : ""}
                  </span>
                  {value.direction && (
                    <span className={styles.labReportSeverity}>
                      {String(value.direction).toLowerCase() === "high"
                        ? "HIGH"
                        : "LOW"}
                    </span>
                  )}
                </div>
              ))}

              {abnormalValues.length > 6 && (
                <div className={styles.labReportRow}>
                  <span className={styles.labReportOthers}>
                    and {abnormalValues.length - 6} others
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className={styles.labReportRow}>
              <span className={styles.labReportParameter}>None</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

interface DischargeSummaryContentProps {
  insightsData?: ActionableInsight[];
}

const DischargeSummaryContent: React.FC<DischargeSummaryContentProps> = ({
  insightsData,
}) => {
  const dischargeSummary = insightsData?.find(
    (item) => item.type === "DISCHARGE_SUMMARY_INSIGHT"
  );

  if (dischargeSummary) {
    const {
      reason_for_admission,
      key_diagnosis,
      treatment_procedure_done,
      discharge_advice_followup,
    } = dischargeSummary.value;

    return (
      <>
        {reason_for_admission && !shouldHideContent(reason_for_admission) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Reason for Admission:</h3>
            <p className={styles.sectionContent}>{reason_for_admission}</p>
          </div>
        )}
        {key_diagnosis && !shouldHideContent(key_diagnosis) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>Key Diagnosis:</h3>
            <p className={styles.sectionContent}>{key_diagnosis}</p>
          </div>
        )}
        {treatment_procedure_done &&
          !shouldHideContent(treatment_procedure_done) && (
            <div className={styles.contentSection}>
              <h3 className={styles.sectionTitle}>Treatment/Procedure Done:</h3>
              <p className={styles.sectionContent}>{treatment_procedure_done}</p>
            </div>
          )}
        {!shouldHideContent(discharge_advice_followup) && (
          <div className={styles.contentSection}>
            <h3 className={styles.sectionTitle}>What's Next:</h3>
            {discharge_advice_followup ? (
              <p className={styles.sectionContent}>
                {discharge_advice_followup}
              </p>
            ) : (
              <p className={styles.sectionContent}>
                Follow up with your healthcare provider as advised.
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <AIContent
      content={dischargeSummary?.summary}
      whatsNextTitle="What's Next"
      whatsNextContent=""
    />
  );
};

interface ContentProps {
  content?: string;
  whatsNextTitle?: string;
  whatsNextContent?: string | string[];
}

const AIContent: React.FC<ContentProps> = ({
  content,
  whatsNextTitle = "What's next",
  whatsNextContent,
}) => {
  const parseContentIntoSections = (text: string) => {
    const lines = text.split(/\\n|\n/).filter((line) => line.trim());

    const sections: Array<{
      subheading: string;
      content: string[];
    }> = [];

    let currentSection: { subheading: string; content: string[] } | null = null;

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      if (trimmedLine.endsWith(":")) {
        if (currentSection) {
          sections.push(currentSection);
        }
        const normalizedSubheading =
          trimmedLine === "Symptoms / Reason for Visit:"
            ? "Reason for visit:"
            : trimmedLine;
        currentSection = {
          subheading: normalizedSubheading,
          content: [],
        };
      } else if (currentSection) {
        currentSection.content.push(trimmedLine);
      } else {
        currentSection = {
          subheading: "",
          content: [trimmedLine],
        };
      }
    });

    if (currentSection) {
      sections.push(currentSection);
    }

    return sections;
  };

  const sections = content ? parseContentIntoSections(content) : [];

  return (
    <>
      {sections.map((section, index) => (
        <div
          key={`section-${section.subheading || "default"}-${index}`}
          className={styles.contentSection}
        >
          {section.subheading && (
            <h3 className={styles.sectionTitle}>{section.subheading}</h3>
          )}
          {section.content.map((contentLine, lineIndex) => (
            <p
              key={`content-${
                section.subheading || "default"
              }-${lineIndex}-${contentLine.slice(0, 10).replace(/\s+/g, "-")}`}
              className={styles.sectionContent}
            >
              {contentLine}
            </p>
          ))}
        </div>
      ))}

      <div className={styles.contentSection}>
        <h3 className={styles.sectionTitle}>{whatsNextTitle}</h3>
        {whatsNextContent && (
          <>
            {Array.isArray(whatsNextContent) ? (
              whatsNextContent.map((contentItem, index) => (
                <p
                  key={`whats-next-content-${contentItem
                    .slice(0, 20)
                    .replace(/\s+/g, "-")}-${index}`}
                  className={styles.sectionContent}
                >
                  {contentItem}
                </p>
              ))
            ) : (
              <>
                {parseContentIntoSections(whatsNextContent).map(
                  (section, sectionIndex) => (
                    <div
                      key={`whats-next-section-${
                        section.subheading || "default"
                      }-${sectionIndex}`}
                    >
                      {section.content.map((contentLine, lineIndex) => (
                        <p
                          key={`whats-next-content-${
                            section.subheading || "default"
                          }-${lineIndex}-${contentLine
                            .slice(0, 10)
                            .replace(/\s+/g, "-")}`}
                          className={styles.sectionContent}
                        >
                          {contentLine}
                        </p>
                      ))}
                    </div>
                  )
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

// ----------- Structured prescription rendering (new shape) -----------

interface PrescriptionMedicineValue {
  name: string;
  dosage?: string | null;
  frequency?: string | null;
}

interface PrescriptionInsightValueShape {
  symptoms?: string;
  diagnosis?: string[];
  medicines?: PrescriptionMedicineValue[];
  nextSteps?: string;
}

const isValidMedicineName = (name: unknown): name is string =>
  typeof name === "string" && name.trim().length > 0 && name.trim() !== "null";

/**
 * True when the actionable_insight.value carries the structured
 * prescription shape the new shallow-insights generator writes. Callers
 * use this as the routing gate between the structured renderer and the
 * legacy text-summary AIContent fallback.
 */
const hasStructuredPrescriptionValue = (
  value: any
): value is PrescriptionInsightValueShape => {
  if (!value || typeof value !== "object") return false;
  const v = value as PrescriptionInsightValueShape;
  const hasMedicines =
    Array.isArray(v.medicines) && v.medicines.some((m) => isValidMedicineName(m?.name));
  const hasDiagnosis =
    Array.isArray(v.diagnosis) &&
    v.diagnosis.some((d) => typeof d === "string" && d.trim());
  // Both fields present as arrays (even empty) means the pipeline wrote this
  // value — take the structured path so we render "No medications listed"
  // instead of falling back to the raw text summary (which may contain "null").
  const hasStructuredShape =
    Array.isArray(v.medicines) && Array.isArray(v.diagnosis);
  return hasMedicines || hasDiagnosis || hasStructuredShape;
};

const PrescriptionStructuredContent: React.FC<{
  insight: ActionableInsight;
}> = ({ insight }) => {
  const { t } = useTranslation();
  const value = (insight.value ?? {}) as PrescriptionInsightValueShape;
  const symptoms = (value.symptoms ?? "").trim();
  const diagnosis = (value.diagnosis ?? []).filter(
    (d) => typeof d === "string" && d.trim()
  );
  const rawMedicines = Array.isArray(value.medicines) ? value.medicines : null;
  const medicines = (rawMedicines ?? []).filter((m) => isValidMedicineName(m?.name));
  const showNoMedications = rawMedicines !== null && medicines.length === 0;
  const nextSteps = (value.nextSteps ?? "").trim();

  return (
    <>
      {symptoms && (
        <div className={styles.contentSection}>
          <h3 className={styles.sectionTitle}>Reason for visit:</h3>
          <p className={styles.sectionContent}>{symptoms}</p>
        </div>
      )}

      {diagnosis.length > 0 && (
        <div className={styles.contentSection}>
          <h3 className={styles.sectionTitle}>Diagnosis:</h3>
          <p className={styles.sectionContent}>{diagnosis.join(", ")}</p>
        </div>
      )}

      {(medicines.length > 0 || showNoMedications) && (
        <div className={styles.contentSection}>
          <h3 className={styles.sectionTitle}>Medicines:</h3>
          {showNoMedications ? (
            <p className={styles.sectionContent}>{t("common.noMedicationsListed")}</p>
          ) : (
            medicines.map((m, idx) => {
              const meta: string[] = [];
              if (m.dosage) meta.push(m.dosage);
              if (m.frequency) meta.push(m.frequency);
              return (
                <p key={`${m.name}-${idx}`} className={styles.sectionContent}>
                  {`• ${m.name}`}
                  {meta.length > 0 ? ` — ${meta.join(" · ")}` : ""}
                </p>
              );
            })
          )}
        </div>
      )}

      {nextSteps && !shouldHideContent(nextSteps) && (
        <div className={styles.contentSection}>
          <h3 className={styles.sectionTitle}>What's next:</h3>
          <p className={styles.sectionContent}>{nextSteps}</p>
        </div>
      )}
    </>
  );
};

export default MAIModal;
